import { Text } from '@mntn/ui-components'
import { CreateCampaignOrExperimentButton } from '@/components/shared'
import { CampaignInfo } from '@/components/shared/campaign-info'
import type { ArchiveCampaignCallback, DuplicateCampaignCallback } from '@/components/shared/types'
import { checkCanCreateCampaign } from '@/components/shared/utils'
import type {
  CreateCampaignExperimentInput,
  CreateCampaignInput,
  OrderItem,
} from '@/flight-deck-api/services/order-items.service'
import { useAuthenticatedSession } from '@/providers/session'
import { EmptyState } from './empty-state'

interface Props {
  // todo: just pass in needed values from order item
  selectedOffering: OrderItem
  onCreateCampaign: (input: CreateCampaignInput) => Promise<void>
  onCreateExperiment: (input: CreateCampaignExperimentInput) => Promise<void>
  onDuplicateCampaign: DuplicateCampaignCallback
  onArchiveCampaign: ArchiveCampaignCallback
  isLoading?: boolean
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const CampaignsSection: React.FC<Props> = ({
  selectedOffering,
  onCreateCampaign,
  onCreateExperiment,
  onDuplicateCampaign,
  onArchiveCampaign,
  isLoading = false,
}) => {
  const { session } = useAuthenticatedSession()
  const {
    airEndTime,
    impressionMetrics: { unallocated: unallocatedImpressions },
  } = selectedOffering

  const hasExistingCampaigns = selectedOffering.campaignGroups.length > 0
  const canCreateExperiment = session.loginAs === 'admin' && selectedOffering.isTest

  const { canCreateCampaign } = checkCanCreateCampaign({
    airEndTime,
    unallocatedImpressions,
  })

  return (
    <div>
      <div className="flex justify-between items-center gap-6 mb-6">
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <Text variant="h4">Campaigns</Text>
          <Text variant="base-sm" className="text-secondary">
            Total number of impressions of this deal can be allocated to multiple campaigns.
          </Text>
        </div>

        <div className="flex-shrink-0">
          <CreateCampaignOrExperimentButton
            variant="core"
            buttonSize="small"
            isLoading={isLoading}
            onCreateCampaign={onCreateCampaign}
            onCreateExperiment={onCreateExperiment}
            orderItem={selectedOffering}
            className="border-none shadow-md rounded-sm"
          />
        </div>
      </div>
      {hasExistingCampaigns ? (
        <CampaignInfo
          campaigns={selectedOffering.campaignGroups}
          onDuplicateCampaign={onDuplicateCampaign}
          onArchiveCampaign={onArchiveCampaign}
          layout="compact"
          isLoading={isLoading}
        />
      ) : (
        <EmptyState
          onCreateCampaign={() =>
            onCreateCampaign({
              isTest: session.loginAs === 'demo',
            })
          }
          canCreateCampaign={canCreateCampaign && !canCreateExperiment}
        />
      )}
    </div>
  )
}
